// Shared dialog behavior. It never changes authentication, API calls or form data.
const dialogs = [];
const previousInert = new Map();
let previousOverflow = null;
let previousDocumentOverflow = null;
let sequence = 0;

function topDialog() {
  return dialogs[dialogs.length - 1];
}

function focusable(element) {
  return Array.from(element.querySelectorAll('a[href],button,input,select,textarea,[tabindex]')).filter(node =>
    !node.disabled && node.tabIndex >= 0 && !node.closest('[inert]') &&
    node.getClientRects().length && getComputedStyle(node).visibility !== 'hidden'
  );
}

function syncBackground() {
  previousInert.forEach((value, node) => { node.inert = value; });
  previousInert.clear();
  const current = topDialog();
  if (current) current.element.inert = false;
  document.body.classList.toggle('sendi-dialog-open', !!current);
  if (!current) {
    if (previousOverflow !== null) document.body.style.overflow = previousOverflow;
    if (previousDocumentOverflow !== null) document.documentElement.style.overflow = previousDocumentOverflow;
    previousOverflow = null;
    previousDocumentOverflow = null;
    return;
  }
  if (previousOverflow === null) previousOverflow = document.body.style.overflow;
  if (previousDocumentOverflow === null) previousDocumentOverflow = document.documentElement.style.overflow;
  document.body.style.overflow = 'hidden';
  document.documentElement.style.overflow = 'hidden';
  for (let child = current.element; child.parentElement && child.parentElement !== document.documentElement; child = child.parentElement) {
    Array.from(child.parentElement.children).forEach(sibling => {
      if (sibling === child || ['SCRIPT', 'STYLE', 'LINK'].includes(sibling.tagName)) return;
      previousInert.set(sibling, sibling.inert);
      sibling.inert = true;
    });
  }
}

function closeRecord(element, restoreFocus = true) {
  const index = dialogs.findIndex(dialog => dialog.element === element);
  if (index < 0) return;
  const [record] = dialogs.splice(index, 1);
  syncBackground();
  if (restoreFocus && !topDialog() && record.trigger?.isConnected && !record.trigger.closest('[inert],[hidden]')) {
    record.trigger.focus({ preventScroll: true });
  }
}

function update(element, binding) {
  const value = binding.value || {};
  const open = !!value.open;
  element.classList.add('sendi-a11y-dialog');
  element.setAttribute('role', 'dialog');
  element.setAttribute('aria-modal', 'true');
  element.tabIndex = -1;
  const headings = Array.from(element.querySelectorAll('h1,h2,h3'));
  const heading = headings.find(node => node.closest('article')) || headings.find(node => node.getClientRects().length) || headings[0];
  if (heading) {
    if (!heading.id) heading.id = `sendi-dialog-title-${++sequence}`;
    element.setAttribute('aria-labelledby', heading.id);
  }
  if (!heading && !element.hasAttribute('aria-label')) element.setAttribute('aria-label', 'Información de Sendi');
  if (!open) {
    closeRecord(element);
    element.hidden = true;
    element.inert = true;
    element.setAttribute('aria-hidden', 'true');
    return;
  }
  element.hidden = false;
  element.inert = false;
  element.removeAttribute('aria-hidden');
  const existing = dialogs.find(record => record.element === element);
  if (existing) { existing.close = value.close; return; }
  dialogs.push({element, close: value.close, trigger: document.activeElement});
  syncBackground();
  requestAnimationFrame(() => {
    if (topDialog()?.element !== element) return;
    if (heading) { heading.tabIndex = -1; heading.focus({preventScroll: true}); }
    else (focusable(element)[0] || element).focus({preventScroll: true});
  });
}

document.addEventListener('keydown', event => {
  const current = topDialog();
  if (!current) return;
  if (event.key === 'Escape' && typeof current.close === 'function') {
    event.preventDefault();
    event.stopPropagation();
    current.close();
  } else if (event.key === 'Tab') {
    const controls = focusable(current.element);
    const first = controls[0];
    const last = controls[controls.length - 1];
    if (!first) { event.preventDefault(); current.element.focus(); }
    else if (event.shiftKey && (document.activeElement === first || !controls.includes(document.activeElement))) {
      event.preventDefault(); last.focus();
    } else if (!event.shiftKey && (document.activeElement === last || !current.element.contains(document.activeElement))) {
      event.preventDefault(); first.focus();
    }
  }
}, true);

export default {mounted: update, updated: update, beforeUnmount(element) { closeRecord(element); }};

// Adapts the existing class-driven side panels, retaining their route handlers.
export const classDrivenDialog = {
  mounted(element, binding) {
    let wasOpen;
    const sync = () => {
      const open = !element.classList.contains('oculto');
      if (open === wasOpen) return;
      wasOpen = open;
      update(element, {value: {open, close: binding.value}});
      const trigger = document.getElementById('menu-btn');
      if (trigger) trigger.setAttribute('aria-expanded', String(open));
    };
    const observer = new MutationObserver(sync);
    observer.observe(element, {attributes: true, attributeFilter: ['class']});
    element._sendiMenuObserver = observer;
    element.addEventListener('click', event => { if (event.target.closest('a[href]') && typeof binding.value === 'function') binding.value(); });
    sync();
  },
  beforeUnmount(element) { element._sendiMenuObserver?.disconnect(); closeRecord(element); }
};
